import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { RxDashboard } from "react-icons/rx";
import { MdOutlineCategory } from "react-icons/md";
import { RiCoupon3Line } from "react-icons/ri";
import { FiLogOut, FiMoon, FiSun, FiCopy, FiClipboard } from "react-icons/fi";
import { FaChevronDown, FaCog, FaUserShield } from "react-icons/fa";
import { AiOutlineMenuFold, AiOutlineMenuUnfold } from "react-icons/ai";
import { motion, AnimatePresence } from "framer-motion";
import { GrAction } from "react-icons/gr";
import { IoGitMergeOutline } from "react-icons/io5";
import { GrUserManager } from "react-icons/gr";
import AyushmanBharatLogo from "../../images/Ayushman_Bharat_logo.png";
import { logout as logoutAction } from "../../redux/actions/authActions";

const Sidebar = ({ isCollapsed, setIsCollapsed, darkMode, setDarkMode }) => {
  const [openMenu, setOpenMenu] = React.useState("");
  const permissions = useSelector((state) => {
    if (state.auth?.permissions && state.auth.permissions.length > 0) {
      return state.auth.permissions;
    }
    // Otherwise, get from localStorage
    const stored = localStorage.getItem("permissions");
    return stored ? JSON.parse(stored) : [];
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [showLogoutModal, setShowLogoutModal] = useState(false);

  const hasPermission = (perm) => permissions.includes(perm);

  const linkClass = (isActive) =>
    classNames(
      "flex items-center font-medium rounded-xl transition-all duration-200 group",
      {
        "bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg shadow-orange-500/25":
          isActive,
        "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800":
          !isActive,
      },
      isCollapsed ? "justify-center p-3" : "gap-3 py-3 px-3",
    );

  const toggleMenu = (menu) => {
    setOpenMenu((prev) => (prev === menu ? "" : menu));
  };

  // const openLogoutModal = () => {
  //   setShowLogoutModal(true);
  // };

  const handleLogoutClick = () => {
    dispatch(logoutAction());
    window.location.href = "/auth";
  };

  const handleLogout = () => {
    dispatch(logoutAction());
    // setShowLogoutModal(false);
    navigate("/auth", { replace: true });
  };

  return (
    <div
      className={classNames(
        "bg-white dark:bg-gray-900 h-screen sticky top-0 flex flex-col shadow-xl transition-all duration-300 overflow-hidden border-r border-gray-200 dark:border-gray-700",
        { "w-80": !isCollapsed, "w-20": isCollapsed },
      )}
    >
      {/* Header */}
      <div
        className={classNames(
          "flex items-center",
          isCollapsed ? "p-4 flex-col gap-4" : "p-6 justify-between",
        )}
      >
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg overflow-hidden">
                <img
                  src={AyushmanBharatLogo}
                  alt="Ayushman Bharat Logo"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
              <div>
                <span className="font-bold text-xl text-gray-900 dark:text-white block">
                  NSAP
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Survey Dashboard
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {darkMode ? (
                  <FiMoon className="text-lg" />
                ) : (
                  <FiSun className="text-lg" />
                )}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Collapse"
              >
                <AiOutlineMenuFold className="text-lg" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center shadow-lg overflow-hidden">
                <img
                  src={AyushmanBharatLogo}
                  alt="Ayushman Bharat Logo"
                  className="w-full h-full object-cover rounded-full"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {/* {darkMode ? <FiSun className="text-lg" /> : <FiMoon className="text-lg" />} */}
                {darkMode ? (
                  <FiMoon className="text-lg" />
                ) : (
                  <FiSun className="text-lg" />
                )}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Expand"
              >
                <AiOutlineMenuUnfold className="text-lg" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav
        className={classNames(
          "flex flex-col gap-2 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent",
          isCollapsed ? "px-2" : "px-3",
        )}
      >
        {hasPermission("view_dashboard_stats") && (
          <NavLink
            to="/dashboard"
            end
            className={({ isActive }) => linkClass(isActive)}
            title="Dashboard"
          >
            <div
              className={classNames(
                "rounded-lg transition",
                isCollapsed
                  ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10"
                  : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10",
              )}
            >
              <RxDashboard
                className={classNames(
                  "text-gray-600 dark:text-gray-400 group-hover:text-orange-500 transition",
                  isCollapsed ? "text-xl" : "text-xl",
                )}
              />
            </div>
            {!isCollapsed && <span className="font-medium">Dashboard</span>}
          </NavLink>
        )}

        {hasPermission("view_survey_officers") && (
          <SidebarGroup
            title="User Management"
            icon={<GrUserManager />}
            menuKey="user"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("view_survey_officers") && (
              <NavLink
                to="/dashboard/survey-officers"
                className={({ isActive }) => linkClass(isActive)}
                title="Survey Officers"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Survey Officers
                  </>
                )}
              </NavLink>
            )}
            {/* ULB-Level Users */}
            {hasPermission("view_ulb_users") && (
              <NavLink
                to="/dashboard/ulb-users"
                className={({ isActive }) => linkClass(isActive)}
                title="ULB-Level Users"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    ULB-Level Users
                  </>
                )}
              </NavLink>
            )}

            {/*Constituency */}

            {hasPermission("view_constituencies") && (
              <NavLink
                to="/dashboard/constituencies"
                className={({ isActive }) => linkClass(isActive)}
                title="Constituencies"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Constituencies
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}
        {hasPermission("districts_view") && (
          <SidebarGroup
            title="ULB Management"
            icon={<FiCopy />}
            menuKey="ulb"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("districts_view") && (
              <NavLink
                to="/dashboard/View-districts"
                className={({ isActive }) => linkClass(isActive)}
                title="District Details"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    District Details
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("districts_create") && (
              <NavLink
                to="/dashboard/Create-Districts"
                className={({ isActive }) => linkClass(isActive)}
                title="Create District"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create District
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_view") && (
              <NavLink
                to="/dashboard/View-Municipalitie"
                className={({ isActive }) => linkClass(isActive)}
                title="Municipalities"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Municipalities
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_create") && (
              <NavLink
                to="/dashboard/Create-Municipality"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Municipalities"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Municipalities
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_admin_view") && (
              <NavLink
                to="/dashboard/View-Admin"
                className={({ isActive }) => linkClass(isActive)}
                title="ULB Admin"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    ULB Admin
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("municipalities_admin_create") && (
              <NavLink
                to="/dashboard/Create-Admin"
                className={({ isActive }) => linkClass(isActive)}
                title="Create ULB Admin"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create ULB Admin
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {/* Survey Management */}
        {(hasPermission("view_beneficiaries") ||
          hasPermission("add_beneficiary") ||
          hasPermission("view_beneficiary_survey")) && (
          <SidebarGroup
            title="Survey Management"
            icon={<FiClipboard />}
            menuKey="survey"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("view_beneficiaries") && (
              <NavLink
                to="/dashboard/beneficiaries"
                className={({ isActive }) => linkClass(isActive)}
                title="Beneficiaries"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Beneficiaries
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("view_beneficiary_survey") && (
              <NavLink
                to="/dashboard/beneficiary-survey"
                className={({ isActive }) => linkClass(isActive)}
                title="Beneficiary Survey"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Beneficiary Survey
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {(hasPermission("create_role ") ||
          hasPermission("create_permission ") ||
          hasPermission("assign_permission_to_role ")) && (
          <SidebarGroup
            title="Role Management"
            icon={<FaUserShield />}
            menuKey="role"
            isCollapsed={isCollapsed}
            openMenu={openMenu}
            toggleMenu={toggleMenu}
          >
            {hasPermission("create_role ") && (
              <NavLink
                to="/dashboard/create-role"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Role"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Role
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("create_permission ") && (
              <NavLink
                to="/dashboard/create-permission"
                className={({ isActive }) => linkClass(isActive)}
                title="Create Permission"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Create Permission
                  </>
                )}
              </NavLink>
            )}
            {hasPermission("assign_permission_to_role ") && (
              <NavLink
                to="/dashboard/assign-permission"
                className={({ isActive }) => linkClass(isActive)}
                title="Assign Permission"
              >
                {!isCollapsed && (
                  <>
                    <div className="w-1 h-1 rounded-full bg-gray-400 ml-1" />
                    Assign Permission
                  </>
                )}
              </NavLink>
            )}
          </SidebarGroup>
        )}

        {hasPermission("view_geofence") && (
          <>
            <NavLink
              to="/dashboard/geofence"
              className={({ isActive }) => linkClass(isActive)}
              title="Geofence"
            >
              <div
                className={classNames(
                  "rounded-lg transition",
                  isCollapsed
                    ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10"
                    : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10",
                )}
              >
                <IoGitMergeOutline
                  className={classNames(
                    "text-gray-600 dark:text-gray-400 group-hover:text-orange-500 transition",
                    isCollapsed ? "text-xl" : "text-xl",
                  )}
                />
              </div>
              {!isCollapsed && "Geofence"}
            </NavLink>
            <NavLink
              to="/dashboard/parkGeofence"
              className={({ isActive }) => linkClass(isActive)}
              title="Park Geofence"
            >
              <div
                className={classNames(
                  "rounded-lg transition",
                  isCollapsed
                    ? "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10"
                    : "p-2 bg-gray-100 dark:bg-gray-800 group-hover:bg-orange-500/10",
                )}
              >
                <IoGitMergeOutline
                  className={classNames(
                    "text-gray-600 dark:text-gray-400 group-hover:text-orange-500 transition",
                    isCollapsed ? "text-xl" : "text-xl",
                  )}
                />
              </div>
              {!isCollapsed && "Park Geofence"}
            </NavLink>
          </>
        )}
      </nav>

      {/* Logout Button */}
      <div className={classNames(isCollapsed ? "p-2" : "p-3")}>
        <button
          // onClick={openLogoutModal}
          onClick={handleLogoutClick}
          className={classNames(
            "flex items-center font-medium rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group w-full",
            isCollapsed ? "justify-center p-3" : "gap-3 py-3 px-3",
          )}
          title="Logout"
        >
          <div
            className={classNames(
              "rounded-lg transition",
              isCollapsed
                ? "p-2 bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50"
                : "p-2 bg-red-100 dark:bg-red-900/30 group-hover:bg-red-200 dark:group-hover:bg-red-900/50",
            )}
          >
            <FiLogOut
              className={classNames(isCollapsed ? "text-xl" : "text-xl")}
            />
          </div>
          {!isCollapsed && "Logout"}
        </button>
      </div>
      {/* Logout Confirmation Modal */}
      {/* {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <FiLogOut className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Confirm Logout
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to logout? You will need to login again to
                access your account.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                >
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

const SidebarGroup = ({
  title,
  icon,
  menuKey,
  isCollapsed,
  openMenu,
  toggleMenu,
  children,
}) => {
  const groupButtonClass = classNames(
    "flex items-center w-full rounded-xl transition-all duration-200 group",
    {
      "bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white":
        openMenu === menuKey,
      "text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800":
        openMenu !== menuKey,
    },
    isCollapsed ? "justify-center p-3" : "justify-between py-3 px-3",
  );

  return (
    <div>
      <button
        onClick={() => toggleMenu(menuKey)}
        className={groupButtonClass}
        title={isCollapsed ? title : undefined}
      >
        <div className="flex items-center gap-3">
          <div
            className={classNames(
              "rounded-lg transition",
              isCollapsed ? "p-2" : "p-2",
              {
                "bg-orange-500/10 text-orange-600 dark:text-orange-400":
                  openMenu === menuKey,
                "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-orange-500/10 group-hover:text-orange-500":
                  openMenu !== menuKey,
              },
            )}
          >
            {icon}
          </div>
          {!isCollapsed && <span className="font-medium">{title}</span>}
        </div>
        {!isCollapsed && (
          <FaChevronDown
            className={classNames(
              "transition-transform duration-200 text-gray-400",
              {
                "rotate-180": openMenu === menuKey,
              },
            )}
          />
        )}
      </button>

      <AnimatePresence>
        {openMenu === menuKey && !isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-4 pl-6 mt-2 flex flex-col gap-1 overflow-hidden border-l border-gray-200 dark:border-gray-700"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Sidebar;
